/**
 * RecordPickerDialog.jsx - pick records by hand for the main project links
 * (10/14/26): "Add existing projects" on a record (several, checkboxes) and
 * the table's "Group under..." (one main project). A search over project #,
 * name, PO #, customer and quote #; the same customer's records first. A
 * record that already sits under another project says so, and choosing it
 * asks before moving it (`confirmMoves`).
 */
import { useMemo, useState } from 'react';
import { Modal } from '../../ui/Modal';
import { BTN_CANCEL, BTN_PRIMARY } from '../../opportunities/editOpportunityConstants';
import { parentIdOf, recordLabel, recordSearchText } from '../../../data/projectHierarchy';

const MAX_SHOWN = 200;

/**
 * Props:
 *   title, intro      the dialog's words
 *   candidates        the records that may be picked
 *   all               every record (to name a candidate's current main project)
 *   multiple          checkboxes (else one, radio)
 *   customer          listed first
 *   targetId          the main project the picks go under (a candidate already under it is not "moving")
 *   confirmMoves      ask before moving records that sit under another project
 *   confirmLabel      the button
 *   onPick(records)   async; answers { failed: [{ error }] } or nothing - the dialog closes when nothing failed
 *   onClose
 */
export default function RecordPickerDialog({
  title,
  intro = null,
  candidates = [],
  all = [],
  multiple = false,
  customer = '',
  targetId = null,
  confirmMoves = false,
  confirmLabel = 'Save',
  onPick,
  onClose,
}) {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState(() => new Set());
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const byId = useMemo(() => new Map((all || []).map((r) => [String(r.id), r])), [all]);
  const cust = String(customer || '')
    .trim()
    .toLowerCase();
  const list = useMemo(
    () =>
      candidates
        .map((r) => ({
          r,
          text: recordSearchText(r),
          mine:
            String(r.customer || '')
              .trim()
              .toLowerCase() === cust,
        }))
        .sort((a, b) => b.mine - a.mine || recordLabel(a.r).localeCompare(recordLabel(b.r))),
    [candidates, cust]
  );
  const q = query.trim().toLowerCase();
  const shown = (q ? list.filter((x) => x.text.includes(q)) : list).slice(0, MAX_SHOWN);
  const chosen = candidates.filter((r) => picked.has(String(r.id)));
  const moving = chosen.filter((r) => parentIdOf(r) && parentIdOf(r) !== String(targetId));
  const underOf = (r) => {
    const p = parentIdOf(r);
    return p && p !== String(targetId) ? byId.get(p) : null;
  };

  const toggle = (id) =>
    setPicked((cur) => {
      const next = multiple ? new Set(cur) : new Set();
      if (cur.has(id) && multiple) next.delete(id);
      else next.add(id);
      return next;
    });
  const go = async () => {
    if (!chosen.length || busy) return;
    if (confirmMoves && moving.length && !asking) {
      setAsking(true);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const out = await onPick(chosen);
      if (out && out.failed && out.failed.length) {
        setError(out.failed.map((f) => f.error).join(' | '));
        setAsking(false);
        return;
      }
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={title}
      onClose={busy ? undefined : onClose}
      width={680}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" style={BTN_CANCEL} onClick={asking ? () => setAsking(false) : onClose} disabled={busy}>
            {asking ? 'Back' : 'Cancel'}
          </button>
          <button
            type="button"
            style={{ ...BTN_PRIMARY, opacity: !chosen.length || busy ? 0.6 : 1 }}
            onClick={go}
            disabled={!chosen.length || busy}
          >
            {busy
              ? 'Saving...'
              : asking
                ? `Move ${moving.length} and save`
                : `${confirmLabel}${multiple && chosen.length ? ` (${chosen.length})` : ''}`}
          </button>
        </div>
      }
    >
      {asking ? (
        <div role="alert" data-testid="confirm-move">
          <div style={{ fontSize: 13, fontWeight: 700, color: '#92400E', marginBottom: 8 }}>
            {moving.length === 1
              ? 'This record already sits under another project.'
              : `${moving.length} records already sit under another project.`}{' '}
            Move {moving.length === 1 ? 'it' : 'them'}?
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.6 }}>
            {moving.map((r) => (
              <li key={r.id}>
                <b>{recordLabel(r)}</b> - now under {recordLabel(underOf(r)) || parentIdOf(r)}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <>
          {intro && <div style={{ fontSize: 12, color: '#444', lineHeight: 1.5, marginBottom: 10 }}>{intro}</div>}
          <input
            autoFocus
            aria-label="Search projects"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by project #, name, PO #, customer..."
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '8px 10px',
              border: '1px solid #D8DED4',
              borderRadius: 6,
              fontSize: 12.5,
              marginBottom: 8,
            }}
          />
          <div
            role="listbox"
            aria-multiselectable={multiple}
            aria-label="Projects"
            style={{ maxHeight: '46vh', overflowY: 'auto', border: '1px solid #E5E9DD', borderRadius: 6 }}
          >
            {shown.length === 0 && <div style={{ padding: 14, fontSize: 12, color: '#888' }}>No project matches.</div>}
            {shown.map(({ r }) => {
              const id = String(r.id);
              const on = picked.has(id);
              const under = underOf(r);
              return (
                <label
                  key={id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    padding: '7px 10px',
                    borderBottom: '1px solid #F0F2EC',
                    cursor: 'pointer',
                    background: on ? '#F1F8E9' : '#fff',
                  }}
                >
                  <input
                    type={multiple ? 'checkbox' : 'radio'}
                    name="record-pick"
                    checked={on}
                    onChange={() => toggle(id)}
                    aria-label={recordLabel(r)}
                    style={{ marginTop: 2 }}
                  />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#0D0D0D' }}>{recordLabel(r)}</span>
                    <span style={{ display: 'block', fontSize: 10.5, color: '#777' }}>
                      {[r.customer, r.status].filter(Boolean).join(' · ')}
                      {under && <span style={{ marginLeft: 6, color: '#B45309', fontWeight: 700 }}>under {recordLabel(under)}</span>}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
          <div style={{ fontSize: 10.5, color: '#888', marginTop: 6 }}>
            {q ? `${shown.length}${shown.length === MAX_SHOWN ? '+' : ''} match` : `${list.length} projects, the same customer's first`}
            {multiple && chosen.length ? ` · ${chosen.length} picked` : ''}
          </div>
        </>
      )}
      {error && (
        <div role="alert" style={{ marginTop: 10, color: '#B71C1C', fontSize: 12, fontWeight: 600 }}>
          {error}
        </div>
      )}
    </Modal>
  );
}
